import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import type { ReceiptListItemDTO } from "../api/client";
import { billingApi } from "../api/client";

export const RECEIPTS_EXPORT_PAGE_SIZE = 200;

function escapeCsvCell(value: string | number | null | undefined): string {
  const s = value == null ? "" : String(value);
  if (
    s.includes(",") ||
    s.includes('"') ||
    s.includes("\n") ||
    s.includes("\r")
  ) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function downloadCsv(filename: string, rows: string[]) {
  const csv = rows.join("\n");
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function formatDateCell(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toISOString().slice(0, 10);
}

export async function fetchAllReceiptsForExport(
  query: Omit<OffsetPageQueryDTO, "offset" | "limit"> & {
    limit?: number;
  },
): Promise<ReceiptListItemDTO[]> {
  const all: ReceiptListItemDTO[] = [];
  let offset = 0;
  let total = Number.POSITIVE_INFINITY;
  const limit = query.limit ?? RECEIPTS_EXPORT_PAGE_SIZE;

  while (offset < total) {
    const page = await billingApi.receipts.list({
      ...query,
      offset,
      limit,
    });
    all.push(...page.data);
    total = page.meta.total;
    offset += limit;
    if (page.data.length === 0) break;
  }

  return all;
}

export function exportReceiptsCsv(receipts: ReceiptListItemDTO[]) {
  const header = [
    "ເລກທີ",
    "ວັນທີອອກ",
    "ຜູ້ອອກ",
    "ຍອດເດີມ",
    "ສ່ວນຫຼຸດ",
    "ປະເພດສ່ວນຫຼຸດ",
    "ຄ່າສ່ວນຫຼຸດ",
    "ຍອດສຸດທ້າຍ",
  ].join(",");

  const body = receipts.map((r) =>
    [
      escapeCsvCell(r.receiptNo),
      escapeCsvCell(formatDateCell(r.issuedAt)),
      escapeCsvCell(r.issuedByName),
      escapeCsvCell(r.originalAmount ?? r.totalAmount),
      escapeCsvCell(r.discountAmount ?? 0),
      escapeCsvCell(r.discountType),
      escapeCsvCell(r.discountValue),
      escapeCsvCell(r.totalAmount),
    ].join(","),
  );

  const stamp = new Date().toISOString().slice(0, 10);
  downloadCsv(`receipts-${stamp}.csv`, [header, ...body]);
}
